import type { MedicalExamFile, MedicalExamFileKind } from '../../types/medicalExams';
import { isValidIsoDate } from '../../utils/masks';
import {
  ACCEPTED_MEDICAL_EXAM_MIME_TYPES,
  MEDICAL_EXAM_TYPE_LABEL,
} from './medicalExams.constants';

const ACCEPTED_SET = new Set<string>(
  ACCEPTED_MEDICAL_EXAM_MIME_TYPES.map((type) => type.toLowerCase()),
);

export const createExamFileId = (): string => (
  `exam-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
);

export const getTodayDateOnly = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isAcceptedMedicalExamFile = (file: File): boolean => {
  const mime = file.type.toLowerCase();
  if (ACCEPTED_SET.has(mime)) {
    return true;
  }

  const name = file.name.toLowerCase();
  return name.endsWith('.pdf')
    || name.endsWith('.jpg')
    || name.endsWith('.jpeg')
    || name.endsWith('.png');
};

export const resolveExamFileKind = (file: File): MedicalExamFileKind => {
  const mime = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (mime === 'application/pdf' || name.endsWith('.pdf')) {
    return 'pdf';
  }

  return 'image';
};

export const resolveExamMimeType = (file: File): string => {
  if (file.type) {
    return file.type.toLowerCase();
  }

  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) {
    return 'application/pdf';
  }
  if (name.endsWith('.png')) {
    return 'image/png';
  }
  return 'image/jpeg';
};

export const createLocalExamFile = (file: File): MedicalExamFile => {
  const mimeType = resolveExamMimeType(file);
  return {
    id: createExamFileId(),
    fileName: file.name,
    mimeType,
    kind: resolveExamFileKind(file),
    sizeBytes: file.size,
    examDate: getTodayDateOnly(),
    previewUrl: URL.createObjectURL(file),
    file,
  };
};

export const hasValidExamDates = (files: MedicalExamFile[]): boolean => (
  files.length > 0 && files.every((file) => isValidIsoDate(file.examDate))
);

export const revokeLocalExamFile = (examFile: MedicalExamFile) => {
  if (!examFile.isDemoAsset && examFile.previewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(examFile.previewUrl);
  }
};

export const formatExamFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

export const formatExamFileTypeLabel = (mimeType: string): string => (
  MEDICAL_EXAM_TYPE_LABEL[mimeType.toLowerCase()]
  ?? mimeType.split('/').pop()?.toUpperCase()
  ?? 'Arquivo'
);
